import styled, { keyframes } from 'styled-components/macro';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import bookOpen from '../../assets/icons/book-open.svg';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const selectedStyles = (bool: boolean): { [key: string]: any } => {
  return bool
    ? {
        color: '#1d4ed6',
        border: '1px solid #1d4ed6',
        backgroundColor: '#ffffff',
      }
    : {
        color: '#1f2939',
        border: '1px solid transparent',
        backgroundColor: '#f2f5f8',
      };
};

export const Wrapper = styled.div<{ canEdit: boolean }>`
  box-sizing: border-box;
  position: relative;
  padding: 24px 0px 5px;
  border-radius: 12px;
  background-color: #ffffff;
  border: 2px solid #ced5de};
  margin: 8px 0;
  transition: all 300ms;
  min-height: ${(props) => (props.canEdit ? '120px' : '72px')};
`;

export const LoaderWrapper = styled.div`
  position: absolute;
  width: fit-content;
  top: 50%;
  left: 50%;
  transform: translateY(-50%);
`;

export const BookWrapper = styled.div`
  height: 16px;
  position: absolute;
  align-items: center;
  top: 0;
  left: 10px;
  transform: translateY(-50%);
  background-color: #ffffff;
  border-radius: 12px;
  padding: 0 8px;
`;

export const BookIcon = styled.img.attrs({ src: bookOpen })`
  width: 16px;
  height: 16px;
`;

export const FontButtonsWrapper = styled.div`
  height: 24px;
  position: absolute;
  top: 0;
  right: 10px;
  transform: translateY(-50%);
  background-color: #ffffff;
  border-radius: 12px;
  padding: 0 8px;
  display: flex;
  gap: 12px;
`;

export const FontButton = styled.button<{
  isSelected: boolean;
  variant: 'big' | 'small';
}>`
  font-size: ${(props) => (props.variant === 'big' ? '16px' : '14px')};
  font-weight: 400;
  line-height: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 300ms;
  ${(props) => selectedStyles(props.isSelected)}
`;

export const Trigger = styled(DropdownMenu.Trigger)`
  width: 24px;
  text-align: center;
  padding: 4px 8px;
  background-color: #f2f5f8;
  color: #1f2939;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  border-radius: 8px;
  transition: all 150ms;
  outline: none;
  border: none;

  :hover {
    background-color: #e4e8ee;
  }
`;

export const Content = styled(DropdownMenu.Content)`
  z-index: 99999;
  background-color: #ffffff;
  width: 360px;
  padding: 12px 8px 12px 12px;
  border-radius: 12px;
  box-shadow: 0 4px 8px 0 rgba(15, 23, 40, 0.04),
    0 12px 18px 0 rgba(15, 23, 40, 0.12), 0 0 10px 6px rgba(15, 23, 40, 0.04);
`;

export const ScrollWrapper = styled.div`
  max-height: 310px;
  overflow-y: auto;
  padding-right: 4px;

  ::-webkit-scrollbar {
    width: 4px;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-track {
    background: inherit;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #e4e8ee;
    border-radius: 10px;

    :hover {
      background-color: #9da6b4;
    }
  }
`;

export const Line = styled.div`
  display: flex;
  gap: 16px;
  border-top: 1px solid #e4e8ee;

  :first-child {
    border: none;
    margin-bottom: 8px;
  }
`;

export const Header = styled.div`
  color: #495466;
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
`;

export const HeaderDate = styled(Header)`
  min-width: 88px;
`;

export const HeaderWorker = styled(Header)`
  width: 100%;
`;

export const Item = styled.div`
  color: #1f2939;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  padding: 6px 0;
`;

export const ItemDate = styled(Item)`
  min-width: 88px;
`;

export const ItemWorker = styled(Item)`
  width: 100%;
`;

export const CellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  align-items: center;
  margin: 12px 8px 0 8px;
`;

export const TextArea = styled.textarea<{ isBig: boolean }>`
  color: #1f2939;
  font-size: ${(props) => (props.isBig ? '18px' : '16px')};
  font-weight: 400;
  line-height: 24px;
  transition: all 300ms;
  width: calc(100% - 16px);
  padding: 0 8px;
  height: 68px;
  overflow-y: auto;
  outline: none;
  border: none;
  resize: none;

  ::-webkit-scrollbar {
    width: 4px;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-track {
    background: inherit;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #e4e8ee;
    border-radius: 10px;

    &:hover {
      background-color: #9da6b4;
    }
  }

  :disabled {
    background-color: inherit;
  }
`;

export const SymbolsCounter = styled.div<{ isDisable: boolean }>`
  color: ${(props) => (props.isDisable ? 'rgb(235, 77, 64)' : '#1f2939')};
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
`;

export const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Placeholder = styled.div`
  padding: 26px 20px 20px;
  color: #98a3b4;
  font-size: 14px;
  text-align: center;
`;

export const ProvidersTabs = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  margin-bottom: 16px;
  position: absolute;
  left: 48px;
  top: -16px;
  background-color: white;
  margin: 0 22px;
  padding: 0 4px;
  padding-bottom: 4px;

  max-width: calc(100% - 168px);
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;

  &::-webkit-scrollbar {
    height: 4px;
    width: 4px;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background: inherit;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #e4e8ee;
    border-radius: 10px;

    :hover {
      background-color: #9da6b4;
    }
  }
`;

export const ProviderTab = styled.button<{ isActive?: boolean }>`
  flex: 0 0 auto; // запрещаем сжиматься
  max-width: 200px; // ограничение ширины вкладки
  border: ${({ isActive }) =>
    isActive ? '1px solid rgb(45, 103, 216)' : '1px solid transparent'};
  background-color: ${({ isActive }) =>
    isActive ? 'rgb(255, 255, 255)' : 'rgb(242, 245, 248)'};
  color: ${({ isActive }) =>
    isActive ? 'rgb(45, 103, 216)' : 'rgb(31, 41, 57)'};
  height: 24px;
  padding: 4px 12px;
  border-radius: 6px;
  outline: none;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  box-sizing: border-box;
  cursor: pointer;
  transition: background-color 150ms;
  font-family: Roboto, sans-serif !important;

  // обрезаем текст
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    background-color: ${({ isActive }) =>
      isActive ? 'rgb(255, 255, 255)' : 'rgb(228, 232, 238)'};
    color: ${({ isActive }) =>
      isActive ? 'rgb(29, 78, 214)' : 'rgb(31, 41, 57)'};
    border: ${({ isActive }) =>
      isActive ? '1px solid rgb(29, 78, 214)' : '1px solid transparent'};
  
`;

export const AnimatedAlgorithmBody = styled.div`
  min-height: 84px;
  height: 84px;
  resize: vertical;
  overflow: auto;
  padding: 0px 16px 0px;
  animation: ${fadeIn} 0.4s ease;

  // скрываем стандартный уголок ресайза
  ::-webkit-resizer {
    display: none;
  }

  &::after {
    content: '';
    position: absolute;
    right: 6px;
    bottom: 6px;
    width: 14px;
    height: 14px;
    pointer-events: none;

    background: repeating-linear-gradient(
      135deg,
      #b7bcc4,
      #b7bcc4 1px,
      transparent 1px,
      transparent 4px
    );

    clip-path: polygon(100% 0, 100% 100%, 0 100%);
    opacity: 0.7;
  }
`;

export const AlgorithmBody = styled.div<{ isBig?: boolean }>`
  font-size: ${({ isBig }) => (isBig ? '18px' : '14px')};
  line-height: 1.6;
  overflow-y: auto;

  p {
    margin: 0 0 8px 0;
  }

  li {
    margin-bottom: 4px;
  }

  ::-webkit-scrollbar {
    width: 4px;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-track {
    background: inherit;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #e4e8ee;
    border-radius: 10px;

    :hover {
      background-color: #9da6b4;
    }
  }
`;
